import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { Paper, MenuItem, Typography, TextField } from "@mui/material";
import { Line } from "react-chartjs-2";
import { format } from "date-fns";
import { getSubmissionsByDayStats } from "../utils";
import SubmissionTypeChip from "../../../../components/SubmissionTypeChip";
import { Activity } from "react-feather";
import Last14Chip from "../../../../components/Table/Last14Chip";
import { ENGAGEMENT_TYPES } from "../../../../storage/constants";
import "chart.js/auto";

const StyledTextField = styled(TextField)`
  width: 160px;

  @media (max-width: 600px) {
    display: none;
  }
`;

const Wrapper = styled(Paper)`
  padding: 20px;
  border-radius: 6px;

  @media (max-width: 600px) {
    padding: 10px;
  }
`;

const UpperWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const ChartWrapper = styled.div`
  width: 100%;
  height: 250px;
  background: white;
`;

const SUBTYPES = {
  SUBMISSIONS: "SUBMISSIONS",
  COMPLETIONS: "COMPLETIONS",
};

function createGradient(ctx, area) {
  const gradient = ctx.createLinearGradient(0, area.bottom, 0, area.top);
  gradient.addColorStop(0, "rgba(158,158,158,0.08)");
  gradient.addColorStop(1, "rgba(158,158,158,0.3)");
  return gradient;
}

const HomeEngagementChart = ({
  submissions,
  engagementType,
  isVendor = false,
}) => {
  const chartRef = useRef(null);
  const [chartMetric, setChartMetric] = useState(SUBTYPES.SUBMISSIONS);
  const [chartData, setChartData] = useState({
    datasets: [],
  });

  const daySubmissions = getSubmissionsByDayStats(
    submissions,
    engagementType,
    isVendor,
  );

  const dataOne = Object.values(daySubmissions)?.map((obj) => obj?.subs);
  const dataTwo = Object.values(daySubmissions)?.map((obj) => obj?.pass);

  const dataUiVars = {
    borderColor: "rgba(158,158,158,.05)",
    pointBorderColor: "slategrey",
    pointBorderWidth: 0,
    pointBackgroundColor: "black",
    borderWidth: 0,
    tension: 0.4,
  };

  useEffect(() => {
    const chart = chartRef.current;
    if (chart) {
      const chartDataPre = {
        labels: chartLabels,
        datasets: [
          {
            data: chartMetric === SUBTYPES.SUBMISSIONS ? dataOne : dataTwo,
            ...dataUiVars,
            fill: {
              target: "origin",
              above: createGradient(chart.ctx, chart.chartArea),
            },
          },
        ],
      };
      setChartData(chartDataPre);
    }

    return undefined;
  }, [chartMetric, engagementType]);

  const chartLabels = Object.keys(daySubmissions)?.map((val) =>
    format(new Date(val), "MMM-dd"),
  );

  const returnTooltipText = () => {
    if (chartMetric === SUBTYPES.SUBMISSIONS) {
      return "Submissions";
    }
    if (chartMetric === SUBTYPES.COMPLETIONS) {
      return "Completions";
    }
    return null;
  };

  let highestVal = 0;
  dataOne.forEach((val) => {
    if (val > highestVal) {
      highestVal = val;
    }
  });

  const options = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: () => "",
          beforeBody: (context) => `${context[0].raw} ${returnTooltipText()} `,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        suggestedMax: highestVal + 2,
        grid: {
          color: "transparent",
        },
        ticks: {
          precision: 0,
        },
      },
      x: {
        grid: {
          color: "transparent",
        },
      },
    },
  };

  return (
    <Wrapper>
      <UpperWrapper>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 6,
          }}
        >
          {engagementType === ENGAGEMENT_TYPES.ALL ? (
            <Activity
              height={14}
              width={14}
              color="#337ab7"
              style={{ marginRight: 2 }}
            />
          ) : (
            <SubmissionTypeChip
              small
              submissionType={engagementType}
              isVendor={isVendor}
            />
          )}

          <Typography style={{ fontSize: 16, fontWeight: "600" }}>
            Pulse Check{" "}
          </Typography>
          <Last14Chip />
        </div>
        <StyledTextField
          select
          size="small"
          variant="outlined"
          color="secondary"
          value={chartMetric}
          style={{ width: 160 }}
          onChange={(e) => setChartMetric(e.target.value)}
        >
          <MenuItem value={SUBTYPES.SUBMISSIONS}>Submissons</MenuItem>
          <MenuItem value={SUBTYPES.COMPLETIONS}>Completions</MenuItem>
        </StyledTextField>
      </UpperWrapper>
      <div style={{ height: 6 }} />
      <ChartWrapper>
        <Line ref={chartRef} data={chartData} options={options} />
      </ChartWrapper>
    </Wrapper>
  );
};

export default HomeEngagementChart;
